// ─── Astria GCC v2 — Gulf tone engine (dialect register). Account-level
// setting (backend field `gccToneMode` on the User model), distinct from the
// device-local `astriaGCCV2Language` (en/ar) toggle: the tone mode only
// shapes *how* Arabic replies sound (formal MSA vs. Gulf vs. Kuwaiti), not
// which language is used. Persisted via PUT /backend/user/:id so it's
// remembered across devices, same as region.

export type GCCV2ToneMode = "msa_fusha" | "gulf" | "kuwaiti";

export const GCC_V2_TONE_MODE_OPTIONS: {
  value: GCCV2ToneMode;
  label: { en: string; ar: string };
}[] = [
  { value: "msa_fusha", label: { en: "Formal (MSA)", ar: "فصحى" } },
  { value: "gulf", label: { en: "Gulf", ar: "خليجي" } },
  { value: "kuwaiti", label: { en: "Kuwaiti", ar: "كويتي" } },
];

export const DEFAULT_GCC_V2_TONE_MODE: GCCV2ToneMode = "gulf";

export const isGCCV2ToneMode = (value: unknown): value is GCCV2ToneMode =>
  value === "msa_fusha" || value === "gulf" || value === "kuwaiti";

// Deterministic, static sample lines shown so the user can "feel" a tone
// before sending a message — no LLM call, so it's instant. Kept separate
// from the actual system prompt blocks in astriaGCCService.js; this is
// display-only copy, not something the model ever sees.
export const GCC_V2_TONE_PREVIEW: Record<
  GCCV2ToneMode,
  { en: string; ar: string; markers: string[] }
> = {
  gulf: {
    en: "How are you doing today? Take it easy for a bit… we'll go step by step, together.",
    ar: "ها شلونك اليوم؟ خلّك هادي شوي… نمشيها خطوة خطوة سوا.",
    markers: ["شلونك", "خلّك", "شوي", "سوا"],
  },
  kuwaiti: {
    en: "Hey, what's up? Your day's a lot lighter now… let's finish it together.",
    ar: "هلا شخبارك؟ يومك اليوم وايد أخف… خلّ نكمّلها سوا.",
    markers: ["شخبارك", "وايد", "هالسالفة"],
  },
  msa_fusha: {
    en: "How was your day? Let's organize your thoughts calmly.",
    ar: "كيف كان يومك؟ دعنا نرتّب أفكارك بهدوء.",
    markers: [],
  },
};
